import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import FinishedProductService from '../../../shared/services/finished-product/finished-product.service';
import { useEffect, useState } from 'react';
import { IFinishedProduct } from '../../../shared/services/finished-product/finished-product.model';
import { Box, Button, Stack, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import gaiaTheme from '../../../root-components/theme';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { Add } from '@mui/icons-material';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { SearchBar } from '../../presentation-components/display-elements-components/search-bar';
import { a11yProps } from '../../../shared/utils/misc-shared-utils';
import { CustomTabPanel } from '../../presentation-components/display-elements-components/custom-tab-panel';
import ProductComponentService from '../../../shared/services/product-component/product-component.service';
import { IProductComponent } from '../../../shared/services/product-component/product-component.model';
import { useDebounce } from '../../../hooks/use-debounce-hook';
import moment from 'moment';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageTitle } from '../../presentation-components/display-elements-components/page-title';

const kgFormatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 3 });

export const ProductsPage = () => {
  const navigate = useNavigate();
  const finishedProductService = new FinishedProductService();
  const productComponentService = new ProductComponentService();
  const [searchParams, setSearchParams] = useSearchParams()
  const [finishedProducts, setFinishedProducts] = useState<IFinishedProduct[]>()
  const [productComponents, setProductComponents] = useState<IProductComponent[]>()
  const [selected, setSelected] = useState<string[]>([]);
  // Search is per tab so text typed on Finished products does not silently
  // pre-filter Product components.
  const [productSearch, setProductSearch] = useState('')
  const [componentSearch, setComponentSearch] = useState('')
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  const debouncedProductSearch = useDebounce(productSearch, 400);
  const debouncedComponentSearch = useDebounce(componentSearch, 400);

  // Derive the tab from the URL rather than copying it on mount, so in-page
  // navigation to ?tab=1 switches tabs without a remount.
  const tabValue = searchParams.get('tab') === '1' ? 1 : 0;

  useEffect(() => {
    finishedProductService.getAll()
      .then(response => {
        setFinishedProducts(response)
      })
  }, [])

  useEffect(() => {
    if (tabValue === 1 && !productComponents) {
      productComponentService.getAll()
        .then(response => {
          setProductComponents(response)
        })
    }
  }, [tabValue])

  const headCells: IHeadCell[] = [
    { id: 'name', numeric: false, label: 'Name', sortable: true, isHeading: true, includeInMobile: true },
    { id: 'description', numeric: false, label: 'Description', sortable: false },
    { id: 'kgCarbonEmission', numeric: true, label: 'kg CO2e per unit', sortable: false, align: 'right', includeInMobile: true },
    // Not sortable: the cell holds the DD/MM/YYYY display string, which sorts
    // lexically. Rows are pre-sorted newest first below instead.
    { id: 'createdAt', numeric: false, label: 'Created on', sortable: false, includeInMobile: false },
  ];

  const matchesSearch = (normalisedSearch: string) => (item: { name?: string; description?: string }) =>
    !normalisedSearch ||
    (item.name ?? '').toLowerCase().includes(normalisedSearch) ||
    (item.description ?? '').toLowerCase().includes(normalisedSearch);

  const newestFirst = (a: { createdAt?: Date }, b: { createdAt?: Date }) =>
    moment(b.createdAt ?? 0).valueOf() - moment(a.createdAt ?? 0).valueOf();

  const toRow = (item: IFinishedProduct | IProductComponent) => ({
    id: item.id,
    name: item.name,
    description: item.description || '',
    kgCarbonEmission: item.kgCarbonEmission != null ? kgFormatter.format(item.kgCarbonEmission) : '—',
    createdAt: moment(item.createdAt).format('DD/MM/YYYY'),
  });

  const normalisedProductSearch = debouncedProductSearch.trim().toLowerCase();
  const normalisedComponentSearch = debouncedComponentSearch.trim().toLowerCase();

  const productRows = finishedProducts?.filter(matchesSearch(normalisedProductSearch)).sort(newestFirst).map(toRow);

  const componentRows = productComponents?.filter(matchesSearch(normalisedComponentSearch)).sort(newestFirst).map(toRow);

  const handleSelected = (selectedRowId: string[]) => {
    if (selectedRowId[0] !== undefined) {
      if (tabValue === 0) {
        navigate(`/products/${selectedRowId[0]}`)
      } else if (tabValue === 1) {
        navigate(`/products/component/${selectedRowId[0]}`)
      }
    }
  }

  const changeTabHandler = (event: React.SyntheticEvent, newValue: number) => {
    const next = new URLSearchParams(searchParams);
    if (newValue === 0) {
      next.delete('tab');
    } else {
      next.set('tab', String(newValue));
    }
    setSearchParams(next, { replace: true });
  };


  return (
    <PageOuterContainer>
      <Stack direction={isMobileMode ? 'column' : 'row'} justifyContent='space-between' alignItems={isMobileMode ? 'flex-start' : 'center'} sx={{ mb: 1 }}>
        <PageTitle title='Product Carbon Footprint' />
      </Stack>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction={'row'} justifyContent={'space-between'}>
            <Tabs value={tabValue} onChange={changeTabHandler} aria-label="tabs">
              <Tab label="Finished products" {...a11yProps(0)} />
              <Tab label="Product components" {...a11yProps(1)} />
            </Tabs>
          </Stack>
        </Box>
        <CustomTabPanel value={tabValue} index={0}>
          <Stack direction='row' justifyContent='space-between' alignItems='center' gap={2} flexWrap='wrap' sx={{ width: '100%', mb: 2 }}>
            <Box sx={{ width: { xs: '100%', sm: 320 } }}>
              <SearchBar searchQuery={productSearch} setSearchQuery={setProductSearch} placeholder='Search products' />
            </Box>
            <Button variant='text' component={Link} to='/products/create'>
              <Add sx={{ color: 'primary.main' }} />
              <Typography sx={{ color: 'primary.main', display: 'inline' }}>Add Product</Typography>
            </Button>
          </Stack>
          {
            finishedProducts &&
            <TableDisplay
              key={normalisedProductSearch}
              isMobileMode={isMobileMode}
              headCells={headCells}
              rows={productRows}
              handleSelected={handleSelected}
              selected={selected}
              setSelected={setSelected} />
          }
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={1}>
          <Stack direction='row' justifyContent='space-between' alignItems='center' gap={2} flexWrap='wrap' sx={{ width: '100%', mb: 2 }}>
            <Box sx={{ width: { xs: '100%', sm: 320 } }}>
              <SearchBar searchQuery={componentSearch} setSearchQuery={setComponentSearch} placeholder='Search components' />
            </Box>
            <Button variant='text' component={Link} to='/products/component/create'>
              <Add sx={{ color: 'primary.main' }} />
              <Typography sx={{ color: 'primary.main', display: 'inline' }}>Add Product Component</Typography>
            </Button>
          </Stack>
          {
            productComponents &&
            <TableDisplay
              key={normalisedComponentSearch}
              isMobileMode={isMobileMode}
              headCells={headCells}
              rows={componentRows}
              handleSelected={handleSelected}
              selected={selected}
              setSelected={setSelected} />
          }
        </CustomTabPanel>
      </Box>
    </PageOuterContainer>
  )
}
